import { ApiService } from './apiService';
import { API_ENDPOINTS } from '../config/api';
import { PaginatedResponse } from '../types/api';
import {
  AssignableRole,
  CreateEmployeeRequest,
  EMPLOYEE_ROLE_LABELS,
  Employee,
  EmployeeApiRole,
  EmployeeDetail,
  EmployeeStatus,
  normalizeEmployeeStatus,
  EmployeesQueryParams,
  SearchEmployeesRequest,
  UpdateEmployeeRequest,
} from '../types/employee';
import { appendQueryString } from '../utils/queryParams';

interface EmployeeListItemApi {
  id: number;
  uniqueId?: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  phoneNumber?: string;
  profilePictureUrl?: string | null;
  role: string | null;
  department: string | null;
  status: string;
}

interface EmployeeRawApi {
  user_id: number;
  unique_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  is_blocked: boolean;
  branch_id?: number;
  role?: { name: EmployeeApiRole };
  branch?: { branch_id?: number; name: string; is_active?: boolean };
  user_detail?: { profile_picture_id?: number | null };
}

const withFullName = (employee: Employee): Employee => ({
  ...employee,
  fullName: `${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim(),
});

const mapListItem = (item: EmployeeListItemApi): Employee =>
  withFullName({
    id: item.id,
    uniqueId: item.uniqueId ?? '',
    firstName: item.firstName,
    lastName: item.lastName,
    email: item.email,
    phoneNumber: item.phoneNumber ?? 'N/A',
    profilePictureUrl: item.profilePictureUrl ?? null,
    role: item.role,
    department: item.department,
    status: normalizeEmployeeStatus(item.status),
  });

const mapSearchRow = (row: EmployeeRawApi): Employee =>
  withFullName({
    id: Number(row.user_id),
    uniqueId: row.unique_id,
    firstName: row.first_name,
    lastName: row.last_name,
    email: row.email,
    phoneNumber: 'N/A',
    profilePictureUrl: null,
    role: row.role?.name ? EMPLOYEE_ROLE_LABELS[row.role.name] : null,
    department: row.branch?.name ?? null,
    status: row.is_blocked ? 'deactivated' : 'active',
  });

const mapEmployeeDetail = (row: EmployeeRawApi): EmployeeDetail => ({
  id: Number(row.user_id),
  uniqueId: row.unique_id,
  firstName: row.first_name?.trim() ?? '',
  lastName: row.last_name?.trim() ?? '',
  email: row.email,
  role: row.role?.name ?? 'manager',
  branchId: Number(row.branch?.branch_id ?? row.branch_id ?? 0),
  profilePictureId: row.user_detail?.profile_picture_id
    ? Number(row.user_detail.profile_picture_id)
    : null,
  status: row.is_blocked ? 'deactivated' : 'active',
});

export class EmployeeService extends ApiService {
  async getEmployees(
    params?: EmployeesQueryParams
  ): Promise<PaginatedResponse<Employee>> {
    const page = params?.page ?? 1;
    const limit = params?.limit ?? 10;
    const useStatusFilter = params?.status && params.status !== 'all';

    if (useStatusFilter) {
      const searchBody: SearchEmployeesRequest = {
        page,
        limit,
        search: params?.search,
        branchId: params?.branchId,
        status: params?.status as EmployeeStatus,
      };

      if (params?.role && params.role !== 'all') {
        searchBody.role = params.role as EmployeeApiRole;
      }

      const response = await this.post<PaginatedResponse<EmployeeRawApi>>(
        API_ENDPOINTS.employees.search,
        searchBody
      );

      return {
        ...response,
        data: response.data.map(mapSearchRow),
      };
    }

    const url = appendQueryString(API_ENDPOINTS.employees.list, {
      page,
      limit,
      search: params?.search,
      role: params?.role !== 'all' ? params?.role : undefined,
      branchId: params?.branchId,
    });

    const response = await this.get<PaginatedResponse<EmployeeListItemApi>>(url);
    return {
      ...response,
      data: response.data.map(mapListItem),
    };
  }

  async getEmployeeById(id: string | number): Promise<EmployeeDetail> {
    const row = await this.get<EmployeeRawApi>(API_ENDPOINTS.employees.get(id));
    return mapEmployeeDetail(row);
  }

  async getAssignableRoles(): Promise<AssignableRole[]> {
    return this.get<AssignableRole[]>(API_ENDPOINTS.employees.roles);
  }

  async createEmployee(data: CreateEmployeeRequest): Promise<Employee> {
    const created = await this.post<EmployeeListItemApi>(
      API_ENDPOINTS.employees.create,
      data
    );
    return mapListItem(created);
  }

  async updateEmployee(
    id: string | number,
    data: UpdateEmployeeRequest
  ): Promise<void> {
    await this.patch<void>(API_ENDPOINTS.employees.update(id), data);
  }

  async deactivateEmployee(id: string | number): Promise<void> {
    await this.post<void>(API_ENDPOINTS.employees.deactivate(id));
  }

  async activateEmployee(id: string | number): Promise<void> {
    await this.post<void>(API_ENDPOINTS.employees.activate(id));
  }

  async sendInvite(id: string | number): Promise<void> {
    await this.post<void>(API_ENDPOINTS.employees.sendInvite(id));
  }
}

export const employeeService = new EmployeeService();
